import { apiClient } from '@littlebird/auth-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  SiteCredential,
  SiteCredentialUpdate,
} from './site-credentials.types';

export const siteCredentialsKey = ['site-credentials'] as const;
const siteCredentialFieldNamesKey = (credential: SiteCredential) =>
  [
    ...siteCredentialsKey,
    credential.domain,
    credential.credential_id,
    'field-names',
  ] as const;

const credentialQueryString = (credential: SiteCredential) =>
  new URLSearchParams({
    domain: credential.domain,
    credential_id: credential.credential_id,
  }).toString();

export const useSiteCredentialsQuery = () =>
  useQuery({
    queryKey: siteCredentialsKey,
    queryFn: () =>
      apiClient.get<{ credentials: SiteCredential[] }>(
        '/site_credentials/summaries'
      ),
  });

export const useSiteCredentialFieldNamesQuery = (credential: SiteCredential) =>
  useQuery({
    queryKey: siteCredentialFieldNamesKey(credential),
    queryFn: () =>
      apiClient.get<{ field_names: string[] }>(
        `/site_credentials/field_names?${credentialQueryString(credential)}`
      ),
  });

export const useUpdateSiteCredentialMutation = () =>
  useMutation({
    mutationFn: (update: SiteCredentialUpdate) =>
      apiClient.patch('/site_credentials', update),
  });

export const useDeleteSiteCredentialMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (credential: SiteCredential) =>
      apiClient.delete(
        `/site_credentials?${credentialQueryString(credential)}`
      ),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: siteCredentialsKey }),
  });
};
